/**
 * HoldsPage — the open-hold (rétention) worklist. Each row is an `ouverte` RetenueQc (the estRetenu
 * containment that the validation §8.4 gate consumes); the biologiste opens a manual hold or lifts
 * (lever) one. Lifting retains the row (médico-légal — never a delete). Full 5-state DoD + the
 * module-absent (cloud/unmounted) degradation panel.
 *
 * Data: GET /qc/retenues (cap qc.retenue.read). Mutations: POST /qc/retenues, /qc/retenues/:id/lever.
 */
import { useState } from "react";
import {
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { QC_CAPS } from "../caps";
import { useHolds, useLabId } from "../api";
import type { RetenueQc } from "../types";
import { idStr } from "../types";
import { fmtTime, isModuleAbsent } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { ModuleAbsentState } from "../components/ModuleAbsentState";
import { OpenHoldModal } from "../components/OpenHoldModal";
import { LeverHoldModal } from "../components/LeverHoldModal";

export function HoldsPage() {
  const { t } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(QC_CAPS.retenueRead);
  const canOpen = useCan(QC_CAPS.retenueOpen);
  const canLift = useCan(QC_CAPS.retenueLift);

  const holdsQuery = useHolds(labId, canRead && online);
  const [openModal, setOpenModal] = useState(false);
  const [leverHold, setLeverHold] = useState<RetenueQc | null>(null);

  const columns: ReadonlyArray<Column<RetenueQc>> = [
    { key: "parametre", header: t("qc.holds.col.parametre"), mono: true, sortable: true, sortAccessor: (r) => r.parametre_code, cell: (r) => r.parametre_code },
    {
      key: "automate",
      header: t("qc.holds.col.automate"),
      mono: true,
      cell: (r) =>
        r.automate_id != null && r.automate_id !== "" ? (
          <span className="nv-ltr-island">{idStr(r.automate_id)}</span>
        ) : (
          <span style={{ color: "var(--nv-steel)" }}>{t("qc.holds.automate.all")}</span>
        ),
    },
    {
      key: "debut",
      header: t("qc.holds.col.debut"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.fenetre_debut,
      cell: (r) => <span className="nv-ltr-island">{fmtTime(r.fenetre_debut)}</span>,
    },
    {
      key: "fin",
      header: t("qc.holds.col.fin"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{fmtTime(r.fenetre_fin)}</span>,
    },
    { key: "motif", header: t("qc.holds.col.motif"), cell: (r) => r.motif ?? "—" },
    {
      key: "actions",
      header: t("qc.holds.col.actions"),
      align: "end",
      cell: (r) => (
        <Button size="sm" variant="secondary" disabled={!canLift} onClick={() => setLeverHold(r)}>
          {t("qc.holds.action.lever")}
        </Button>
      ),
    },
  ];

  const actions = (
    <Button disabled={!canOpen} onClick={() => setOpenModal(true)}>
      {t("qc.holds.new")}
    </Button>
  );

  const header = <PageHeader title={t("qc.holds.title")} subtitle={t("qc.holds.subtitle")} actions={actions} />;

  if (!canRead) return <ForbiddenState requiredCapabilities={[QC_CAPS.retenueRead]} />;

  let body: React.ReactNode;
  if (!online) {
    body = <OfflineState onRetry={() => void holdsQuery.refetch()} cached={(holdsQuery.data?.length ?? 0) > 0} />;
  } else if (holdsQuery.isLoading) {
    body = <LoadingState variant="table" rows={5} />;
  } else if (holdsQuery.isError && isModuleAbsent(holdsQuery.error)) {
    body = <ModuleAbsentState />;
  } else if (holdsQuery.isError) {
    body = (
      <ErrorState
        message={holdsQuery.error.message}
        code={holdsQuery.error.code}
        onRetry={() => void holdsQuery.refetch()}
      />
    );
  } else {
    body = (
      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)" }}>
          <Table
            columns={columns}
            rows={holdsQuery.data ?? []}
            rowKey={(r) => idStr(r.id)}
            caption={t("qc.holds.title")}
            emptyTitle={t("qc.holds.empty.title")}
            emptyDescription={t("qc.holds.empty.desc")}
            pageSize={15}
          />
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {header}
      {body}

      <OpenHoldModal open={openModal} onClose={() => setOpenModal(false)} onOpened={() => setOpenModal(false)} />
      <LeverHoldModal
        open={leverHold !== null}
        retenue={leverHold}
        onClose={() => setLeverHold(null)}
        onLifted={() => setLeverHold(null)}
      />
    </div>
  );
}
